import Document from "documents/document";
import UnavailableDocument from "documents/unavailable-documents/unavailable-document";
import { BulkDocumentRequest } from "documents/_api/commands/documents-form-api-request";

type DocumentUploadResult = Document | UnavailableDocument;

export type DocumentUploadSummary = {
  documents: Document[];
  unavailableDocuments: UnavailableDocument[];
  refusedFiles: Array<{ name: string; issue: string }>;
  totalCount: number;
  uploadedCount: number;
  failedCount: number;
};

export type DocumentUploadSnackbar = {
  message: string;
  variant: "success" | "warning" | "error";
};

export function summarizeDocumentUploadResults(
  results: DocumentUploadResult[],
  requests: BulkDocumentRequest[] = [],
): DocumentUploadSummary {
  const documents = results.filter((result): result is Document => result instanceof Document);
  const unavailableDocuments = results.filter(
    (result): result is UnavailableDocument => result instanceof UnavailableDocument
  );
  const requestNamesById = new Map(
    requests.map((request) => [request.requestId.value, request.name?.trim() || request.file.name || "File"]),
  );
  const refusedFiles = unavailableDocuments.map((document, index) => ({
    name: requestNamesById.get(document.id.value) ?? `File ${index + 1}`,
    issue: document.issue,
  }));

  return {
    documents,
    unavailableDocuments,
    refusedFiles,
    totalCount: results.length,
    uploadedCount: documents.length,
    failedCount: unavailableDocuments.length,
  };
}

export function getDocumentUploadSnackbar(summary: DocumentUploadSummary): DocumentUploadSnackbar {
  const failureDetail = summary.refusedFiles.length > 0
    ? `: ${summary.refusedFiles.map(({ name, issue }) => `${name}: ${issue}`).join("; ")}`
    : "";

  if (summary.totalCount === 0) {
    return {
      message: "No files uploaded",
      variant: "warning",
    };
  }

  if (summary.failedCount === 0) {
    return {
      message: summary.uploadedCount === 1 ? "Uploaded file" : `Uploaded ${summary.uploadedCount} files`,
      variant: "success",
    };
  }

  if (summary.uploadedCount === 0) {
    return {
      message: summary.failedCount === 1
        ? `Failed to upload file${failureDetail}`
        : `Failed to upload ${summary.failedCount} files${failureDetail}`,
      variant: "error",
    };
  }

  return {
    message: `Uploaded ${summary.uploadedCount} of ${summary.totalCount} files. ${summary.failedCount} failed${failureDetail}`,
    variant: "warning",
  };
}
